/**
 * ModalManager.js - Modal UI management with accessibility support
 *
 * Responsibilities:
 * - Handle DOM modal display and visibility
 * - Manage accessibility (focus, aria-hidden, inert)
 * - Process keyboard interactions (Escape to close)
 * - Delegate event click resolution to EventClickResolver
 *
 * Key Methods:
 * - initialize() - Modal setup with error handling
 * - show()/hide() - Modal visibility with accessibility-compliant focus management
 * - toggle()/updateContent() - Modal content manipulation
 * - getRecentErrors()/hasCriticalIssues() - Error debugging
 *
 * Note: Event click handling is delegated to EventClickResolver to maintain
 * single responsibility - this class focuses purely on modal UI operations.
 */

import ErrorHandler from '../../utils/ErrorHandler.js';
import EventClickResolver from './EventClickResolver.js';

export default class ModalManager {
  constructor(options = {}) {
    this.errorHandler = new ErrorHandler({
      module: 'ModalManager',
      maxErrors: 20,
      debug: options.debug || false
    });

    this.overlay = null;
    this.modalBody = null;
    this.closeBtn = null;
    this.initialized = false;

    // Accessibility: Track the element that triggered the modal
    this.triggerElement = null;

    // Event delegation handler reference (for cleanup)
    this._listViewClickHandler = null;

    // Event click resolver - handles finding events and data resolution
    this._eventClickResolver = null;

    // Component references for dependency injection
    this._componentRefs = options.componentRefs || {};
  }

  initialize() {
    try {
      this.overlay = document.getElementById('modal-overlay');
      this.modalBody = document.getElementById('modal-body');
      this.closeBtn = this.overlay?.querySelector('.modal-close') || null;

      if (!this.overlay || !this.modalBody) {
        this.errorHandler.handleFatalError('Modal overlay or body not found', null, {
          category: 'initialization',
          action: 'missing-dom-elements',
          hasOverlay: !!this.overlay,
          hasModalBody: !!this.modalBody
        });
        return false;
      }

      // Create event click resolver with callbacks and component refs
      this._eventClickResolver = new EventClickResolver({
        showModal: (content) => this.show(content),
        showEventModal: this._componentRefs.showEventModal || null,
        calendarApp: this._componentRefs.calendarApp,
        eventDataManager: this._componentRefs.eventDataManager,
        calendarRenderer: this._componentRefs.calendarRenderer,
        modalAdapter: this._componentRefs.modalAdapter,
        debug: this.errorHandler.debug
      });

      this._setupEventListeners();
      this._setupListViewEventHandlers();

      this.initialized = true;

      this.errorHandler.logSuccess('ModalManager initialized successfully', {
        category: 'initialization',
        action: 'init-success'
      });

      return true;
    } catch (error) {
      this.errorHandler.handleFatalError('Critical error during ModalManager initialization', error, {
        category: 'initialization',
        action: 'fatal-init-error'
      });
      return false;
    }
  }

  _setupEventListeners() {
    try {
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.hide());
      }
      this.overlay.addEventListener('click', e => {
        if (e.target === this.overlay) this.hide();
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.isVisible()) this.hide();
      });
    } catch (error) {
      this.errorHandler.handleError('Error setting up modal event listeners', error, {
        category: 'initialization',
        action: 'event-listeners-failed'
      });
    }
  }

  _setupListViewEventHandlers() {
    try {
      // Use event delegation on document body for better performance
      if (this._listViewClickHandler) {
        document.body.removeEventListener('click', this._listViewClickHandler);
      }

      this._listViewClickHandler = (e) => {
        const titleDiv = e.target.closest('.event-title.clickable');
        if (titleDiv) {
          // Track trigger element for accessibility focus management
          this.triggerElement = titleDiv;
          // Delegate to resolver
          this._eventClickResolver.handleEventClick(e, titleDiv);
        }
      };

      document.body.addEventListener('click', this._listViewClickHandler);

      const clickableElements = document.querySelectorAll('.event-title.clickable');
      this.errorHandler.logSuccess('List view event handlers set up successfully', {
        category: 'initialization',
        action: 'event-handlers-setup',
        elementCount: clickableElements.length
      });

    } catch (error) {
      this.errorHandler.handleError('Error setting up list view event handlers', error, {
        category: 'initialization',
        action: 'list-handlers-failed'
      });
    }
  }

  /**
   * Refresh list view handlers - can be called externally after DOM updates
   */
  refreshListViewHandlers() {
    if (!this.initialized) return;

    this.errorHandler.logInfo('Refreshing list view handlers', {
      category: 'initialization',
      action: 'refresh-handlers'
    });
    this._setupListViewEventHandlers();
  }

  /**
   * Show modal with proper accessibility and focus management
   * @param {string} content - HTML content to display in modal
   * @param {Element} triggerElement - Optional element that triggered the modal (for focus return)
   * @returns {boolean} Success status
   */
  show(content, triggerElement = null) {
    try {
      if (!this.initialized) {
        this.errorHandler.handleError('Attempted to show modal when not initialized', null, {
          category: 'modal-operation',
          action: 'show-not-initialized'
        });
        return false;
      }
      if (!content) {
        this.errorHandler.handleError('No content provided for modal show', null, {
          category: 'modal-operation',
          action: 'show-no-content'
        });
        return false;
      }

      // Track trigger element if provided (for accessibility)
      if (triggerElement) {
        this.triggerElement = triggerElement;
      }

      this.modalBody.innerHTML = content;
      this.overlay.classList.remove('hidden');

      // Accessibility: Remove inert and update aria-hidden
      if ('inert' in this.overlay) {
        this.overlay.inert = false;
      }
      this.overlay.setAttribute('aria-hidden', 'false');

      document.body.style.overflow = 'hidden';

      // Accessibility: Focus management - focus first focusable element or close button
      requestAnimationFrame(() => {
        const firstFocusable = this.overlay.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const focusTarget = firstFocusable || this.closeBtn || this.overlay;

        if (focusTarget === this.overlay) {
          this.overlay.tabIndex = -1;
        }
        focusTarget.focus();
      });

      return true;
    } catch (error) {
      this.errorHandler.handleFatalError('Critical error showing modal', error, {
        category: 'modal-operation',
        action: 'show-fatal-error'
      });
      return false;
    }
  }

  /**
   * Hide modal with proper accessibility and focus management
   * @returns {boolean} Success status
   */
  hide() {
    try {
      if (!this.initialized) {
        this.errorHandler.handleError('Attempted to hide modal when not initialized', null, {
          category: 'modal-operation',
          action: 'hide-not-initialized'
        });
        return false;
      }

      // Set accessibility attributes immediately
      if ('inert' in this.overlay) {
        this.overlay.inert = true;
      }
      this.overlay.setAttribute('aria-hidden', 'true');

      // Remove the modal from view
      this.overlay.classList.add('hidden');
      document.body.style.overflow = '';

      // Clear content to remove all focusable elements
      this.modalBody.innerHTML = '';

      // Return focus to trigger element if available and visible
      if (this.triggerElement &&
          this.triggerElement.offsetParent !== null &&
          typeof this.triggerElement.focus === 'function') {
        this.triggerElement.focus();
      } else {
        // Blur any focused element within the modal, let browser handle focus
        const focusedElement = document.activeElement;
        if (this.overlay.contains(focusedElement)) {
          focusedElement.blur();
        }
      }

      // Clear trigger reference
      this.triggerElement = null;

      return true;
    } catch (error) {
      this.errorHandler.handleError('Error hiding modal', error, {
        category: 'modal-operation',
        action: 'hide-error'
      });
      return false;
    }
  }

  isVisible() {
    return this.initialized && !this.overlay.classList.contains('hidden');
  }

  isInitialized() {
    return this.initialized;
  }

  toggle(content) {
    return this.isVisible() ? this.hide() : this.show(content);
  }

  updateContent(content) {
    try {
      if (!this.initialized) {
        this.errorHandler.handleError('Attempted to update modal content when not initialized', null, {
          category: 'modal-operation',
          action: 'update-not-initialized'
        });
        return false;
      }
      if (!content) {
        this.errorHandler.handleError('No content provided for modal update', null, {
          category: 'modal-operation',
          action: 'update-no-content'
        });
        return false;
      }
      this.modalBody.innerHTML = content;
      return true;
    } catch (error) {
      this.errorHandler.handleError('Error updating modal content', error, {
        category: 'modal-operation',
        action: 'update-error'
      });
      return false;
    }
  }

  getContent() {
    return this.initialized ? this.modalBody.innerHTML : '';
  }

  /**
   * Update component references after construction (for late binding)
   * @param {Object} refs - Component references to update
   */
  setComponentRefs(refs = {}) {
    this._componentRefs = { ...this._componentRefs, ...refs };

    // Update EventClickResolver refs if already created
    if (this._eventClickResolver) {
      this._eventClickResolver.setComponentRefs(refs);
    }
  }

  destroy() {
    try {
      if (!this.initialized) return;
      if (this.isVisible()) this.hide();

      // Remove event delegation listener
      if (this._listViewClickHandler) {
        document.body.removeEventListener('click', this._listViewClickHandler);
        this._listViewClickHandler = null;
      }

      this.overlay = null;
      this.modalBody = null;
      this.closeBtn = null;
      this.triggerElement = null;
      this._eventClickResolver = null;
      this.initialized = false;

      this.errorHandler.logSuccess('ModalManager destroyed', {
        category: 'initialization',
        action: 'destroy-complete'
      });
    } catch (error) {
      this.errorHandler.handleError('Error during ModalManager destruction', error, {
        category: 'initialization',
        action: 'destroy-error'
      });
    }
  }

  /**
   * Get the event click resolver instance
   * @returns {EventClickResolver|null}
   */
  getEventClickResolver() {
    return this._eventClickResolver;
  }

  /**
   * Get recent errors for debugging
   * @param {number} minutes - Minutes to look back (default: 5)
   * @returns {Array} Recent error entries
   */
  getRecentErrors(minutes = 5) {
    return this.errorHandler.getRecentErrors(minutes);
  }

  /**
   * Check if there are critical issues that need attention
   * @returns {boolean} True if critical issues exist
   */
  hasCriticalIssues() {
    return this.errorHandler.hasCriticalIssues();
  }

  /**
   * Clear error history (useful for testing or after resolving issues)
   */
  clearErrorHistory() {
    this.errorHandler.clearErrorHistory();
  }

  /**
   * Get comprehensive stats including error information
   * @returns {Object} Stats object with error data
   */
  getStats() {
    const errorStats = this.errorHandler.getErrorStats();

    return {
      initialized: this.initialized,
      hasOverlay: !!this.overlay,
      hasModalBody: !!this.modalBody,
      isVisible: this.isVisible(),
      hasTriggerElement: !!this.triggerElement,
      hasEventClickResolver: !!this._eventClickResolver,
      errors: {
        total: errorStats.totalErrors,
        fatal: errorStats.fatalErrors,
        recent: errorStats.recentErrors,
        byCategory: errorStats.errorsByCategory
      }
    };
  }
}
